import { useQuery } from "react-query";

import { LicensesService } from "~/entity/Licenses/api/service";
import { ILicensesOverviewParams } from "~/entity/Licenses/model/schema";

import { parseOwnerValue } from "./owners";
import { ILicenseOwner, ILicenseSelectOption } from "./types";

const PAGE_SIZE = 100;

export interface ILicensePositionTarget {
  instanceId: number;
  positionInstanceId: number;
}

export const buildPositionValue = (
  instanceId: number,
  positionInstanceId: number,
) => `${instanceId}:${positionInstanceId}`;

export const parsePositionValue = (value: string): ILicensePositionTarget => {
  const [instanceId, positionInstanceId] = value.split(":");

  return {
    instanceId: Number(instanceId),
    positionInstanceId: Number(positionInstanceId),
  };
};

export const fetchLicensePositionOptions = async (
  owner: ILicenseOwner,
): Promise<ILicenseSelectOption[]> => {
  const options: ILicenseSelectOption[] = [];
  const ownerParams: ILicensesOverviewParams = {
    dealerId: owner.dealerId,
    organizationId: owner.organizationId,
  };
  let page = 0;
  let total = Infinity;

  while (options.length < total || page === 0) {
    const response = await LicensesService.GET.positionGroups({
      ...ownerParams,
      page,
      count: PAGE_SIZE,
    });
    const data = response.data;

    if (data.level !== "positions" || data.rows.length === 0) {
      break;
    }

    total = data.total;

    data.rows.forEach((row) => {
      if (row.instanceId == null) {
        return;
      }

      row.positions.forEach((position) => {
        options.push({
          value: buildPositionValue(
            row.instanceId as number,
            position.instanceId,
          ),
          label: `${position.code}. ${position.name}`,
        });
      });
    });

    page += 1;

    if (page * PAGE_SIZE >= total) {
      break;
    }
  }

  return options;
};

export const useLicensePositionOptions = (ownerValue?: string | null) => {
  const owner = ownerValue ? parseOwnerValue(ownerValue) : undefined;
  const hasOwner = Boolean(owner && (owner.dealerId || owner.organizationId));

  const { data, isLoading } = useQuery(
    [LicensesService._routes.licenses, "positionOptions", ownerValue],
    () => fetchLicensePositionOptions(owner as ILicenseOwner),
    { enabled: hasOwner },
  );

  return {
    options: hasOwner ? (data ?? []) : [],
    loading: hasOwner && isLoading,
  };
};
